import React from 'react';
import { useLocalSearchParams } from 'expo-router';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTrackEventOnFocus } from '@order/Telemetry';

import { AccountScreenContainer, AccountScreenSection, AccountScreenTitle } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import {
  IdMeAuthorizationWebView,
  isIdMeConfigured,
  useIdMeVerification,
} from '../../integrations/idme';
import { NotFoundScreen } from '../NotFoundScreen';
import { IdMeVerificationContent } from './components';
import { useIdMeVerificationContent, useIdMeVerificationNotices } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const IdMeVerificationScreen = () => {
  const isIdMeVerificationEnabled = useFeatureFlag('CELS-6174-order-id-me-verification-enabled');
  const isLoggedIn = useIsLoggedIn();
  const { entry_source: entrySource } = useLocalSearchParams<RouteParams>();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const isIdMeAvailable = isIdMeVerificationEnabled && isIdMeConfigured;
  const signInRedirect = entrySource
    ? `/account/id-me?entry_source=${encodeURIComponent(entrySource)}`
    : undefined;

  // ───────────────────────────────────────────────────────────────────────────

  if (!isIdMeAvailable) {
    return <NotFoundScreen />;
  }

  if (!isLoggedIn) {
    return <AccountScreenContainer screen="id-me" redirectTo={signInRedirect} />;
  }

  return <IdMeVerificationScreenReady />;
};

// ─────────────────────────────────────────────────────────────────────────────

const IdMeVerificationScreenReady = () => {
  const { entry_source: entrySource } = useLocalSearchParams<RouteParams>();
  const content = useIdMeVerificationContent();
  const notices = useIdMeVerificationNotices({ content });

  const verification = useIdMeVerification({
    entrySource,
    onCancelled: notices.showCancelledNotice,
    onFailed: notices.showFailedNotice,
    onNotVerified: notices.showNotVerifiedNotice,
    onLinkConflict: notices.showLinkConflictNotice,
  });

  useTrackEventOnFocus({
    name: 'idme.verification_page.viewed',
    payload: { entry_source: entrySource },
  });

  const { authorizationRequest, reportAuthorizationResult } = verification;

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <>
      <AccountScreenContainer screen="id-me">
        <AccountScreenTitle title={content.title} />

        <AccountScreenSection>
          <IdMeVerificationContent verification={verification} content={content} />
        </AccountScreenSection>
      </AccountScreenContainer>

      {authorizationRequest ? (
        <IdMeAuthorizationWebView
          request={authorizationRequest}
          onResult={reportAuthorizationResult}
        />
      ) : null}
    </>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type RouteParams = {
  entry_source?: string;
};
